import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { lookupAbdmService } from "api/abdmBridge";
import {
  abdmCheckOutcome,
  abdmFieldForRole,
  abdmIdsHolding,
  abdmLookupHint,
  abdmRegistrationRowsOf,
  baseServiceId,
} from "utils/abdmServiceStatus";

const mono = { fontFamily: "monospace", wordBreak: "break-all" };
const buttonSx = { textTransform: "none", fontWeight: 600, borderRadius: "10px" };

const ROLES = [
  { value: "HIP", label: "HIP (shares records)" },
  { value: "HIU", label: "HIU (requests records)" },
];

const trimmed = (value) => String(value ?? "").trim();

/**
 * Checks any Service ID an admin types against ABDM, as "Verify ABDM IDs" does
 * for a doctor's HIP ID: GET /admin/abdm/service-lookup?serviceId=&role=,
 * ABDM §3.2.6 Find bridge by service id, judged in the chosen role. The
 * answer also lists the doctor profiles holding the id as a HIP, HIU or HFR
 * ID. Nothing is filed on anyone and no doctor is notified. `services` is the
 * bridge's own list (§3.2.7), already on the page, so a typed id can be
 * matched against it before ABDM answers.
 */
const ServiceLookupCard = ({ services = [] }) => {
  const navigate = useNavigate();
  const [serviceId, setServiceId] = useState("");
  const [role, setRole] = useState("HIP");
  const [checking, setChecking] = useState(false);
  const [answer, setAnswer] = useState(null);

  const id = trimmed(serviceId);
  const hint = abdmLookupHint(serviceId);
  const listed = id ? services.find((service) => service.id === id) : undefined;
  const ofFacility =
    id && !listed
      ? services.filter((service) => baseServiceId(service.id) === id && service.id !== id)
      : [];

  const check = async (event) => {
    event?.preventDefault?.();
    if (!id || checking) return;
    setChecking(true);
    const response = await lookupAbdmService(id, role);
    const data = response?.status === 200 ? response.data?.data : null;
    if (data?.result) {
      setAnswer({
        serviceId: id,
        role,
        result: data.result,
        holders: Array.isArray(data.holders) ? data.holders : [],
      });
    } else {
      setAnswer({
        serviceId: id,
        role,
        error: response?.data?.message || "The check failed; try again later.",
      });
    }
    setChecking(false);
  };

  const outcome = answer?.result
    ? abdmCheckOutcome(answer.result, abdmFieldForRole(answer.role), { filed: false })
    : null;
  const registration = answer?.result ? abdmRegistrationRowsOf(answer.result) : [];

  return (
    <Card sx={{ borderRadius: "18px", p: 3 }} data-testid="abdm-lookup">
      <Typography variant="h6" fontWeight={700}>
        Check a Service ID
      </Typography>
      <Typography fontSize="13px" color="#6b7280" mb={2}>
        Ask ABDM whether a facility's Service ID is registered under DRCFO's Bridge ID, in
        the HIP or the HIU role: the check "Verify ABDM IDs" runs on a doctor's profile,
        for any id. Nothing is saved and no doctor is notified.
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box component="form" onSubmit={check} noValidate>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <TextField
              label="Service ID"
              value={serviceId}
              onChange={(event) => setServiceId(event.target.value)}
              fullWidth
              size="small"
              inputProps={{ style: mono, "data-testid": "abdm-lookup-input" }}
              helperText={
                hint ||
                "The facility's Service ID from its ABDM registration: letters, digits and _, never an ABHA number."
              }
              FormHelperTextProps={{
                "data-testid": "abdm-lookup-hint",
                sx: hint ? { color: "#B45309", fontWeight: 600 } : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Role"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              fullWidth
              size="small"
            >
              {ROLES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              startIcon={
                checking ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
              }
              disabled={checking || !id}
              sx={{ ...buttonSx, height: 40 }}
            >
              {checking ? "Checking..." : "Check"}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {listed && (
        <Typography fontSize="13px" color="#6b7280" mt={2} data-testid="abdm-lookup-listed">
          ABDM lists {id} under DRCFO's bridge
          {listed.active === false ? ", marked inactive" : ""}
          {Array.isArray(listed.types) && listed.types.length
            ? ` (${listed.types.join(", ")})`
            : ""}
          ; see the services below.
        </Typography>
      )}
      {ofFacility.length > 0 && (
        <Typography fontSize="13px" color="#6b7280" mt={2} data-testid="abdm-lookup-facility">
          No service is registered as {id} itself, but the facility's numbered services
          under DRCFO's bridge are {ofFacility.map((service) => service.id).join(", ")}.
        </Typography>
      )}

      {answer?.error && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="abdm-lookup-error">
          {answer.error}
        </Alert>
      )}
      {outcome && (
        <Alert severity={outcome.severity} sx={{ mt: 2 }} data-testid="abdm-lookup-outcome">
          {outcome.message}
        </Alert>
      )}
      {registration.length > 0 && (
        <Box mt={3} data-testid="abdm-lookup-registration">
          <Typography fontWeight={700}>Registration in ABDM</Typography>
          <Typography fontSize="13px" color="#6b7280" mb={2}>
            As the ABDM gateway answered. Times are ABDM's own; it gives no time zone.
          </Typography>
          <Grid container spacing={2}>
            {registration.map(({ label, value }) => (
              <Grid item xs={12} sm={4} key={label}>
                <Box mb={1.5}>
                  <Typography fontSize="13px" color="#6b7280" mb={0.5}>
                    {label}
                  </Typography>
                  <Typography fontWeight={600} color="#111827">
                    {value || value === 0 ? value : "-"}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {answer?.result && (
        <Box mt={3}>
          <Typography fontWeight={700} mb={1}>
            Doctor profiles with this id
          </Typography>
          {answer.holders.length === 0 ? (
            <Typography fontSize="13px" color="#6b7280" data-testid="abdm-lookup-no-holders">
              No doctor profile holds {answer.serviceId} as its HIP, HIU or HFR ID.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "12px" }}>
              <Table size="small" data-testid="abdm-lookup-holders">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Held as</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {answer.holders.map((holder) => (
                    <TableRow key={holder._id}>
                      <TableCell sx={{ fontWeight: 600 }}>{holder.name || "-"}</TableCell>
                      <TableCell>{holder.role?.type || "-"}</TableCell>
                      <TableCell>
                        {abdmIdsHolding(holder, answer.serviceId).join(", ") || "-"}
                      </TableCell>
                      <TableCell>
                        {holder.status ? (
                          <Chip
                            size="small"
                            label={holder.status}
                            color={holder.status === "ACTIVE" ? "success" : "default"}
                            sx={{ fontWeight: 600 }}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          sx={buttonSx}
                          onClick={() => navigate(`/doctors/${holder._id}`)}
                        >
                          View profile
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Card>
  );
};

export default ServiceLookupCard;
